<template>
    <div id="form-container" ref="formContainer">
        <div id="pgTitle" tabindex="-1"><h1>{{pageTitle}}</h1></div>

        <form-wizard :stepSize="stepSize"
                     :nextButtonText="buttons.next"
                     :backButtonText="buttons.back"
                     :finishButtonText="buttons.submit"
                     color="#003865"
                     @on-complete="onSubmit"
                     @on-change="switchTabs"
                     id="wizard">
            <span slot="title" tabindex="-1"/>

            <tab-content title="Personal"
                         :before-change="()=>validateStep('personal')"
                         :after-change="()=>scrollTo()">
                <Personal ref="personal" @on-validate="mergePartialModels" :application="application" :hasCertificate="hasCertificate" :textingPreferenceOptions="textingPreferenceOptions"/>
            </tab-content>
            <tab-content title="Endorsement"
                         :before-change="()=>validateStep('endorsement')"
                         :after-change="()=>scrollTo()">
                <Endorsement ref="endorsement" @on-validate="mergePartialModels" :application="application"/>
            </tab-content>
            <tab-content title="Attachments"
                         :before-change="()=>validateStep('att')"
                         :after-change="()=>loadOptionsAndScroll()">
                <Attachments ref="att" @on-validate="mergePartialModels" :application="application" v-bind:current="application.attachments"
                             :attachmentTypeOptions="attachmentTypeOptions"/>
            </tab-content>
            <tab-content title="Affirmation"
                         :before-change="()=>validateStep('confirm')"
                         :after-change="()=>scrollTo()">
                <Signature ref="confirm" @on-validate="mergeCheck" :application="application"/>
            </tab-content>

        </form-wizard>
    </div>
</template>

<script>
    import Personal from "@/components/endorsement-wizard/Personal";
    import Endorsement from "@/components/endorsement-wizard/Endorsement";
    import Attachments from "@/components/endorsement-wizard/Attachments";
    import Signature from "@/components/endorsement-wizard/Signature";
    import {commonOptions} from "@/mixins/commonOptions";
    import {buttons} from "@/mixins/constants"
    import axios from 'axios';
    import axiosRetry from 'axios-retry';
    import {toastService} from "@/mixins/toastService";

    axiosRetry(axios, { retries: 0,
        retryDelay: axiosRetry.exponentialDelay  })

    export default {
        name: "EndorsementApplication",
        components:{
            Personal,
            Endorsement,
            Attachments,
            Signature
        },
        mixins: [
            commonOptions,
            buttons,
            toastService
        ],
        data() {
            return{
                stepSize: 'sm',
                layout: '',
                isSubmitted: false,
                pageTitle: '',
                attachmentTypeOptions: [],
                textingPreferenceOptions: [],
                application: {
                    profile: {
                        firstName: null,
                        lastName: null,
                        middleName: null,
                        ssn: null,
                        dateOfBirth: null,
                        contact:{
                            email: null,
                            phone: null,
                            address:{
                                streetAddress: null,
                                city: null,
                                state: null,
                                postalCode: null,
                            },
                        },
                        textingPreferences: null
                    },
                    endorsement: {
                        testPeriod: null,
                        testDate: null,
                        workPeriod: null,
                        state: null,
                        certificateNumber: null,
                    },
                    facility: {
                        name: null,
                        address:{
                            streetAddress: null,
                            city: null,
                            state: null,
                            postalCode: null
                        },
                        staffingAgency: null
                    }
                },
                isDisabled: false,
                profile: { },
                hasCertificate: null
            }
        },
        created() {
            document.title = 'Nurse Aide Interstate Endorsement Application';
            this.pageTitle = document.title;
            this.loadTextingOptions();
            this.loadCertificate();
            this.loadProfile();
        },
        methods: {
            buttons: function(){
                return buttons;
            },
            onSubmit(){
                if(!this.isDisabled){
                    this.isDisabled = true;
                }
                this.sendToServer();
            },
            sendToServer(){
                axiosRetry(axios, {retries: 0});
                if(!this.application.endorsement.workPeriod){
                  this.application.facility=null;
                }
                axios.post(`${process.env.VUE_APP_API_BASE_URL}/application/interstate`, this.application)
                    .then(response => {
                        this.isSubmitted = true;
                        toastService.showRootToast('Submitted', 'You application for registration via interstate endorsement has been submitted.  ' +
                            'You will receive an email once MDH has reviewed and approved or denied your application.', 'success', this.$root);
                        this.$router.push('/home');
                    }).catch(error => {
                        if(error.response.status===406) {
                            toastService.showRootToast('Error','Identifying information does not match the registered user.  ' +
                                'Please check your submission for entry errors and try again.', 'danger', this.$root);
                        } else if(error.response.status===409) {
                            toastService.showRootToast('Error','An open application already exists. ' +
                                'Please return to your dashboard and resubmit if you need to alter your application.', 'danger', this.$root);
                        } else if(error.response.status===428) {
                            toastService.showRootToast('Error','Existing Certificate: ' +
                                'The identifying information you have provided is associated with an existing certificate.  ' +
                                'You must register as CNA to claim your existing certificate before submitting an Interstate Endorsement application.  ' +
                                'If you do not have an existing certificate please contact MDH.', 'danger',
                                this.$root);
                        } else if(error.response.status===422) {
                            toastService.showRootToast('Error','Invalid social security number: ' +
                                'The Social Security Number you have provided is not valid, please correct this field and try again.  ' +
                                'If you believe that the Social Security Number you have entered is correct, please contact MDH.', 'danger',
                                this.$root);
                        } else {
                            toastService.showRootToast('Error','The application has encountered an issue. Please DO NOT resubmit, ' +
                                'return to the home page to continue', 'danger', this.$root);
                        }
                });
            },
            validateStep(name){
                var refToValidate = this.$refs[name];
                return refToValidate.validate();
            },
            mergePartialModels(model, isValid){
                if(isValid){
                    this.application = Object.assign({}, this.application, model);
                    if(this.application.opts){
                        delete this.application.opts;
                    }
                }
            },
            mergeCheck(model){
                this.isCheck = model['isCheck'];
            },
            scrollTo(){
                let elem = document.getElementById('wizard');
                elem.scrollIntoView();
            },
            focus(e){
                let elem = document.getElementsByClassName('card-header')[e+1];
                elem.focus();
            },
            switchTabs(prevIdx, nextIdx){
                var title = 'Welcome';
                switch(nextIdx){
                    case 0:
                        title = 'Personal Information';
                        break;
                    case 1:
                        title = 'Employment Information';
                        break;
                    case 2:
                        title = 'Attachments';
                        break;
                    case 3:
                        title = 'Affirmation';
                        break;
                    default:
                        title = 'Application';
                }
                document.title = title + ' - Nurse Aide Interstate Endorsement Application - NAR';
                this.pageTitle = document.title;
                document.getElementById('pgTitle').focus();
            },
            removeTabFocus(){
                this.$nextTick(function(){
                    var steps = document.querySelectorAll("div[role='tab']");
                    steps.forEach(step => {
                        step.setAttribute("tabindex", "-1");
                    });
                    var steps2 = document.querySelectorAll('.wizard-nav-pills>li>a');
                    steps2.forEach(step => {
                        step.setAttribute("tabindex","-1");
                        step.setAttribute("aria-disabled", true);
                    })
                });
            },
            loadOptionsAndScroll(){
                this.attachmentTypeOptions = []
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/attachments/public/interstateTypes/` + this.application.endorsement.testPeriod
                    + "/" + this.application.endorsement.workPeriod)
                    .then(response => {
                        for(let type of response.data){
                            this.attachmentTypeOptions.push({
                                value: type.code,
                                text: type.description
                            })
                        }
                        this.scrollTo()
                    });
            },
            loadTextingOptions(){
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/profile/texting/getList`)
                    .then(response=>{
                        for(let option of response.data){
                            this.textingPreferenceOptions.push({
                                value: option.code,
                                text: option.description
                            })
                        }
                    })
            },
           loadCertificate(){
             axios.get(`${process.env.VUE_APP_API_BASE_URL}/certificate/user`)
                 .then(response => {
                   this.hasCertificate = response.data.number != null;
                 }).catch()
           },
          loadProfile(){
              axios.get(`${process.env.VUE_APP_API_BASE_URL}/profile/user`)
                  .then(response=>{
                      if (response.data != null && response.data !== "") {
                          this.application.profile = response.data;
                          this.application.profile.textingPreferences = response.data.textingPreferences.code;
                      }
                  })
                  .catch();
          }
        },
        updated: function(){
            this.removeTabFocus();
        },
        mounted(){
            this.removeTabFocus();
            this.scrollTo();
        }


    }
</script>

<style scoped>

</style>